// MapVoteScreen — multiplayer variant of MapSelectScreen.
//
// Same visual layout (cards in their pickerCard positions, hover float, click
// to commit) but:
//   - Each card carries a vote-count bubble in the top-right.
//   - The title shows "Vote for the map you want... N" with N as the orange
//     countdown.
//   - Clicking a card sends a vote (and re-vote) to the queue room rather
//     than starting the loading flow locally.
//   - The screen resolves with the matchmaking handoff payload from the
//     server's "go" message.
//
// Bundles flagged hiddenFromOnline are excluded from this UI (they don't
// load on the server and would crash matchmaking).

import type { Room } from '@colyseus/sdk';
import { listBundles, type MapBundle } from '../maps/registry';
import { menuAudio } from './MenuAudio';
import { MenuNav, type MenuItem } from './MenuNav';
import { t } from '../i18n';

const STAGE_W = 1920;
const STAGE_H = 1080;
const WIPE_DURATION_MS = 700;
// Mirrors MapSelectScreen's morph clock so the two screens feel identical
// when the winning map cards animate into the big-card layout.
const LOADING_DURATION_MS = 3750;
const MORPH_DURATION_MS = 450;
// Same low-pass sweep MapSelectScreen plays — music ducks behind the
// loading transition so the act of "going into the map" reads through
// the ear as well as the eye.
const MUSIC_SWEEP_TARGET_HZ = 500;
const MUSIC_SWEEP_DURATION_MS = 1400;

export interface GoPayload {
  reservation: unknown;
  mapId: string;
  charId: number;
  roster: unknown;
}

interface QueueStateLike {
  phase: string;
  voteCountdown: number;
  mapVotes: Map<string, number> | { forEach(cb: (v: number, k: string) => void): void };
}

function visibleBundles(): MapBundle[] {
  return listBundles()
    .filter((b) => !b.json.hiddenFromPicker && !b.json.hiddenFromOnline && b.json.pickerCard)
    .sort((a, b) => (a.json.pickerCard!.left - b.json.pickerCard!.left));
}

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#map-vote {
  position: fixed; inset: 0; z-index: 99;
  background: url('/ui/map-selection-bg.webp') center center / cover no-repeat #1a1a1a;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #FFFFFF;
  overflow: hidden;
  clip-path: inset(0 0 0 0);
  transition: clip-path ${WIPE_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1);
}
#map-vote.wiping-out { clip-path: inset(0 100% 0 0); pointer-events: none; }

#map-vote .stage {
  position: absolute; top: 50%; left: 50%;
  width: ${STAGE_W}px; height: ${STAGE_H}px;
  transform-origin: center center;
  transform: translate(-50%, -50%) scale(1);
}

#map-vote .title {
  position: absolute; left: 0; right: 0; top: 80px;
  text-align: center;
  font-size: 48px; line-height: 1;
}
#map-vote .title .countdown {
  color: #FFB84D;
  text-shadow: 0 -3px 0 rgba(155, 60, 0, 0.4);
  display: inline-block;
  min-width: 1ch;
  margin-left: 10px;
}

#map-vote .card {
  --rot: 0deg;
  --base-left: 0px;
  --base-top: 0px;
  position: absolute;
  left: var(--base-left);
  top: var(--base-top);
  width: 400px;
  padding: 12px;
  box-sizing: border-box;
  background: #F6F7F8;
  border-radius: 22px;
  box-shadow: 0px 10px 16px rgba(0,0,0,0.48), inset 0px -4px 2px rgba(0,0,0,0.15);
  cursor: pointer;
  border: 0;
  text-align: center;
  color: inherit;
  font-family: inherit;
  display: block;
  transform-origin: center center;
  transform: rotate(var(--rot));
  translate: 0 0;
  outline: 0 solid rgba(255, 255, 255, 0);
  outline-offset: 3px;
  transition:
    left ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    top ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    width ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    padding ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    border-radius ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    box-shadow ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    transform ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    translate 150ms cubic-bezier(0.2, 0.9, 0.2, 1),
    outline-color 150ms ease,
    outline-width 150ms ease,
    opacity 200ms ease;
}
#map-vote .card:hover, #map-vote .card.focused {
  translate: 0 -4px;
  outline: 4px solid rgba(255, 255, 255, 0.5);
}
#map-vote .card.voted {
  outline: 4px solid #FFB84D;
  box-shadow: 0px 14px 20px rgba(0,0,0,0.55), 0 0 0 4px rgba(255,184,77,0.4), inset 0px -4px 2px rgba(0,0,0,0.15);
}
#map-vote.selected .card { cursor: default; pointer-events: none; }
#map-vote.selected .card:hover,
#map-vote.selected .card.focused {
  translate: 0 0;
  outline: 0 solid rgba(255, 255, 255, 0);
}

#map-vote .card .thumb {
  width: 100%;
  height: 210px;
  border-radius: 20px;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  display: block;
  transition:
    height ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    border-radius ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1);
}
#map-vote .card .label {
  font-size: 32px;
  line-height: 1;
  color: #59A0DE;
  padding: 20px 0;
  margin: 0;
  transition:
    font-size ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    padding ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1);
}

/* Winning-card big-card morph state. Identical layout to MapSelectScreen. */
#map-vote .card.selected {
  left: 400px;
  top: 220px;
  width: 800px;
  padding: 30px;
  border-radius: 80px;
  box-shadow: 0px 28px 34px rgba(0,0,0,0.55), inset 0px -8px 9px rgba(0,0,0,0.15);
  transform: rotate(-1.96deg);
  z-index: 2;
}
#map-vote .card.selected .thumb {
  height: 400px;
  border-radius: 60px;
}
#map-vote .card.selected .label {
  font-size: 64px;
  padding: 30px 0 30px 0;
}
#map-vote .card.dismissing {
  opacity: 0;
  transform: rotate(var(--rot)) translateY(40px) scale(0.9);
  pointer-events: none;
}
#map-vote.selected .title { opacity: 0; }
#map-vote .title { transition: opacity 220ms ease; }

/* Sticky note + progress bar — same shapes as MapSelectScreen so the
   shared muscle memory carries from local → online play. */
#map-vote .sticky {
  position: absolute;
  left: 1160px;
  top: 290px;
  width: 400px;
  height: 450px;
  background:
    linear-gradient(180deg, rgba(250, 156, 34, 0.2) 0%, rgba(250, 156, 34, 0.2) 12.5%, rgba(250, 156, 34, 0) 15.38%),
    #FBE5AB;
  box-shadow: 0px 15px 17px rgba(0,0,0,0.4), inset 0px -6px 9px rgba(0,0,0,0.2);
  border-radius: 30px;
  transform: rotate(2.01deg) translateX(60px);
  opacity: 0;
  pointer-events: none;
  z-index: 1;
  box-sizing: border-box;
  padding: 100px 100px 0 100px;
  font-family: 'Chewy', sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 50px;
  color: #837457;
  transition:
    transform 400ms cubic-bezier(0.3, 0, 0.2, 1) 100ms,
    opacity 400ms ease 100ms;
}
#map-vote .sticky.shown {
  transform: rotate(2.01deg) translateX(0);
  opacity: 1;
}

#map-vote .bar {
  position: absolute;
  left: 50%;
  bottom: 80px;
  transform: translateX(-50%);
  width: 640px;
  height: 24px;
  background: #B8B8B8;
  border-radius: 12px;
  overflow: hidden;
  opacity: 0;
  transition: opacity 220ms ease;
  z-index: 2;
}
#map-vote.selected .bar { opacity: 1; }
#map-vote .bar .fill {
  width: 0%;
  height: 100%;
  background: #FA9C22;
  border-radius: 12px;
  box-shadow: inset 0 2px 0 rgba(255,255,255,0.35), inset 0 -2px 0 rgba(0,0,0,0.15);
  transition: width ${LOADING_DURATION_MS}ms linear;
}
#map-vote .bar.filling .fill { width: 100%; }

/* Vote bubble — top-right of card. Hidden when count = 0. */
#map-vote .card .vote-bubble {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #FA9C22;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 400;
  box-shadow: 0 4px 8px rgba(0,0,0,0.35), inset 0 -3px 0 rgba(0,0,0,0.18);
  border: 3px solid rgba(255,255,255,0.65);
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 180ms ease, transform 180ms cubic-bezier(0.2, 0.9, 0.2, 1);
}
#map-vote .card .vote-bubble.shown {
  opacity: 1;
  transform: scale(1);
}
`;

export class MapVoteScreen {
  private root: HTMLElement | null = null;
  private styleEl: HTMLStyleElement | null = null;
  private resizeHandler = this.updateStageScale.bind(this);
  private nav: MenuNav | null = null;
  private visible: MapBundle[] = [];
  private currentVote: string | null = null;
  private detachStateChange: (() => void) | null = null;
  private resolved = false;

  /** Resolves with the server-issued matchmaking handoff (when the queue
   *  broadcasts `"go"`), or `null` if the queue room closed before handoff.
   *
   *  Once `"go"` arrives, the winning card morphs into a big rotated card,
   *  a sticky-note hint slides in, a progress bar drains while the
   *  caller-supplied `preload` runs, and the screen wipes out — exactly
   *  the same sequence as MapSelectScreen so local + online play feel
   *  identical at the match-entry seam. show() resolves only after the
   *  wipe completes (or immediately with `null` if the room closed).
   *
   *  The `preload` callback receives the full GoPayload (reservation +
   *  charId + roster) so it can do full match infrastructure setup
   *  (consume seat, build canvas, start GameLoop) during the bar drain.
   *  By the time the wipe completes, the game is already rendering
   *  behind the still-fading vote screen. */
  async show(
    room: Room<QueueStateLike>,
    preload?: (go: GoPayload) => Promise<void>,
    onBeforeWipe?: (go: GoPayload) => void,
    onLockedIn?: (go: GoPayload) => void
  ): Promise<GoPayload | null> {
    this.mount();
    const go = await new Promise<GoPayload | null>((resolve) => {
      const finish = (r: GoPayload | null): void => {
        if (this.resolved) return;
        this.resolved = true;
        this.detachStateChange?.();
        resolve(r);
      };

      // Hand the room reference to vote handlers and the countdown updater.
      this.installVoteHandlers(room);

      const update = (state: QueueStateLike): void => {
        this.renderCountdown(state.voteCountdown);
        this.renderVotes(state);
      };
      if ((room.state as QueueStateLike | undefined)?.voteCountdown !== undefined) {
        update(room.state);
      }
      const handler = (state: QueueStateLike): void => update(state);
      room.onStateChange(handler);
      this.detachStateChange = () => {
        try { room.onStateChange(() => { /* noop */ }); } catch { /* noop */ }
      };

      // Server's "go" message is the trigger to leave this screen.
      room.onMessage('go', (msg: GoPayload) => finish(msg));

      // If the queue room closes (server gave up), resolve with null.
      room.onLeave(() => finish(null));
    });

    if (!go || !this.root) return go;

    // Big-card morph — apply selected/dismissing classes, reveal sticky,
    // start the bar fill. Run the preload during the bar's draining window
    // so heavy GLB/shader work overlaps with the animation rather than
    // tacking on a flat loading delay after.
    //
    // Kick off the same low-pass music sweep MapSelectScreen plays on
    // commit so the transition reads through audio + visuals.
    menuAudio.sweepLowpass(MUSIC_SWEEP_TARGET_HZ, MUSIC_SWEEP_DURATION_MS);
    // Vote is locked in — kill the screen's own card-nav (no more focus
    // moves between cards during the morph) and let the caller disable any
    // outer input contexts (PartyPanel B→leave, escape→leave) so the
    // loading window can't be interrupted.
    this.nav?.setEnabled(false);
    onLockedIn?.(go);
    this.enterSelectedState(go.mapId);
    await sleep(500);
    await Promise.all([
      sleep(LOADING_DURATION_MS - 500),
      preload ? preload(go) : Promise.resolve()
    ]);
    await sleep(120);
    onBeforeWipe?.(go);
    await sleep(100);

    this.nav?.dispose();
    this.nav = null;
    menuAudio.playMapScreenSweep();
    this.root.classList.add('wiping-out');
    await sleep(WIPE_DURATION_MS);
    this.dispose();
    return go;
  }

  private enterSelectedState(picked: string): void {
    if (!this.root) return;
    this.root.classList.add('selected');
    const cards = Array.from(this.root.querySelectorAll('.card')) as HTMLButtonElement[];
    for (const card of cards) {
      if (card.dataset.map === picked) {
        card.classList.add('selected');
      } else {
        card.classList.add('dismissing');
      }
    }
    const sticky = this.root.querySelector('.sticky') as HTMLElement | null;
    const bundle = this.visible.find((b) => b.id === picked);
    if (sticky && bundle?.json.loadingHint) sticky.textContent = t(`maps.${bundle.id}.loadingHint`);
    requestAnimationFrame(() => {
      if (!this.root) return;
      const stickyEl = this.root.querySelector('.sticky') as HTMLElement | null;
      if (stickyEl) stickyEl.classList.add('shown');
      const bar = this.root.querySelector('.bar') as HTMLElement | null;
      if (bar) bar.classList.add('filling');
    });
  }

  async leaveScreen(): Promise<void> {
    if (!this.root) return;
    menuAudio.playMapScreenSweep();
    this.nav?.dispose();
    this.nav = null;
    this.root.classList.add('wiping-out');
    await sleep(WIPE_DURATION_MS);
    this.dispose();
  }

  dispose(): void {
    if (!this.root) return;
    removeEventListener('resize', this.resizeHandler);
    this.nav?.dispose();
    this.nav = null;
    this.root.remove();
    this.styleEl?.remove();
    this.root = null;
    this.styleEl = null;
  }

  // -- internals ----------------------------------------------------------

  private mount(): void {
    if (this.root) return;
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLES;
    document.head.appendChild(this.styleEl);

    this.visible = visibleBundles();

    const root = document.createElement('div');
    root.id = 'map-vote';
    root.innerHTML = `
      <div class="stage">
        <div class="title">${t('mapVote.title')}<span class="countdown">10</span></div>
        ${this.visible.map((b) => {
          const card = b.json.pickerCard!;
          return `
            <button
              class="card"
              data-map="${escapeAttr(b.id)}"
              style="--base-left: ${card.left}px; --base-top: ${card.top}px; --rot: ${card.rotationDeg}deg;"
            >
              <div class="vote-bubble">0</div>
              <div class="thumb" style="background-image: url('${escapeAttr(card.thumbnail)}');"></div>
              <div class="label">${escapeHtml(t(`maps.${b.id}.label`))}</div>
            </button>
          `;
        }).join('')}
        <div class="sticky"></div>
        <div class="bar"><div class="fill"></div></div>
      </div>
    `;
    document.body.appendChild(root);
    this.root = root;
    addEventListener('resize', this.resizeHandler);
    this.updateStageScale();
    menuAudio.playMapScreenSweep();
  }

  private installVoteHandlers(room: Room<QueueStateLike>): void {
    const cards = Array.from(this.root!.querySelectorAll('.card')) as HTMLButtonElement[];
    const items: MenuItem[] = cards.map((btn, i) => ({
      el: btn,
      row: 0,
      col: i,
      onActivate: () => {
        const mapId = btn.dataset.map ?? '';
        if (!mapId) return;
        try { room.send('vote', { mapId }); } catch { /* noop */ }
        // Optimistically mark — server will broadcast updated totals back.
        this.currentVote = mapId;
        for (const c of cards) c.classList.toggle('voted', c.dataset.map === mapId);
      }
    }));
    const initialIndex = Math.min(Math.floor(items.length / 2), items.length - 1);
    this.nav = new MenuNav({ items, initialIndex });
    this.nav.mount();
  }

  private renderCountdown(secs: number): void {
    if (!this.root) return;
    const el = this.root.querySelector('.title .countdown') as HTMLElement | null;
    if (el) el.textContent = String(Math.max(0, secs));
  }

  private renderVotes(state: QueueStateLike): void {
    if (!this.root) return;
    const totals = new Map<string, number>();
    if (state.mapVotes && typeof state.mapVotes.forEach === 'function') {
      state.mapVotes.forEach((v: number, k: string) => totals.set(k, v));
    }
    const cards = Array.from(this.root.querySelectorAll('.card')) as HTMLButtonElement[];
    for (const card of cards) {
      const id = card.dataset.map ?? '';
      const count = totals.get(id) ?? 0;
      const bubble = card.querySelector('.vote-bubble') as HTMLElement | null;
      if (!bubble) continue;
      bubble.textContent = String(count);
      bubble.classList.toggle('shown', count > 0);
    }
    void this.currentVote;
  }

  private updateStageScale(): void {
    if (!this.root) return;
    const stage = this.root.querySelector('.stage') as HTMLElement | null;
    if (!stage) return;
    const s = Math.min(innerWidth / STAGE_W, innerHeight / STAGE_H);
    stage.style.transform = `translate(-50%, -50%) scale(${s})`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
function escapeAttr(s: string): string {
  return s.replace(/["<>&]/g, (c) => ({ '"': '&quot;', '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c]!);
}
function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
